import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import useTrackingLinksService from '@/services/tracking-links';

const TrackingLinks = useTrackingLinksService();

export default defineStore('tracking-links', () => {
  const isFetchingLink = ref(false);
  const isFetchingAnalytics = ref(false);
  const isExporting = ref(false);

  // Analytics Dashboard Data
  const trackingLink = ref(null);
  const analytics = ref(null);
  const userMap = ref({});

  const isFetching = computed(() => (
    isFetchingAnalytics.value
    || isFetchingLink.value
  ));

  /**
   * Fetch details about a TrackingLink by ID.
   * @param {number} id - ID of the TrackingLink to fetch.
   * @returns {Promise<void>}
   */
  const fetchTrackingLink = async (id) => {
    isFetchingLink.value = true;

    try {
      const resp = await TrackingLinks.getTrackingLink(id);

      trackingLink.value = resp.data?.value?.link || null;
      userMap.value = resp.data?.value?.users || {};
    } catch (err) {
      console.error('Failed to fetch tracking link:', err);
    }

    isFetchingLink.value = false;
  };

  /**
   * Fetch analytics for a TrackingLink by ID and store in state.
   * @param {number} id - ID of the TrackingLink to fetch stats for.
   * @param {Object} [query={}] - Optional query parameters for the request.
   * @returns {Promise<void>}
   */
  const fetchAnalytics = async (id, query = {}) => {
    isFetchingAnalytics.value = true;

    try {
      const resp = await TrackingLinks.getAnalytics(id, query);
      analytics.value = resp.data?.value || null;
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    }

    isFetchingAnalytics.value = false;
  };

  /**
   * Export visit CSV for a TrackingLink by ID.
   * It will also immediately trigger the browser to download the results as a CSV file.
   * @param {number} id - ID of the TrackingLink to export.
   * @param {Object} [query={}] - Optional query parameters for the export.
   * @returns {Promise<void>}
   */
  const exportVisits = async (id, query = {}) => {
    isExporting.value = true;

    try {
      await TrackingLinks.exportVisits(id, query);
    } catch (err) {
      console.error('Failed to export tracking link visits:', err);
    } finally {
      isExporting.value = false;
    }
  };

  return {
    isFetching,
    isExporting,
    trackingLink,
    analytics,
    userMap,

    fetchTrackingLink,
    fetchAnalytics,
    exportVisits,
  };
});
